export type TextMeasurer = (text: string, font: string) => number;

export type NoCanvasFallback = "zero" | "estimate";

/** Font size in px from a canvas font string, e.g. "lighter 12px sans-serif" → 12. */
function parseFontSizePx(font: string): number {
    const match = /(\d+(?:\.\d+)?)px/.exec(font);
    if (!match) return 12;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : 12;
}

function defaultGetContext(): CanvasRenderingContext2D | null {
    if (typeof document === "undefined") return null;
    return document.createElement("canvas").getContext("2d");
}

/** Fallback font when resetting the shared canvas before each measure. */
const CANVAS_DEFAULT_FONT = "10px sans-serif";

export function createTextMeasurer(options?: {
    getContext?: () => CanvasRenderingContext2D | null;
    whenNoCanvas?: NoCanvasFallback;
}): TextMeasurer {
    const getContext = options?.getContext ?? defaultGetContext;
    const whenNoCanvas = options?.whenNoCanvas ?? "zero";
    const cache = new Map<string, number>();
    let ctx: CanvasRenderingContext2D | null | undefined;
    let fontsReadyHooked = false;

    // Web fonts load after first paint. Widths measured before that use fallback glyphs and are wrong.
    // Clear the cache once fonts are ready. Hook on first measure, not at construction: document may
    // not exist at module load.
    // Callers that memoize measure results must also invalidate after fonts.ready — clearing this
    // cache alone does not re-run React memos.
    function hookFontsReadyOnce(): void {
        if (fontsReadyHooked) return;
        fontsReadyHooked = true;

        const fonts = typeof document === "undefined" ? undefined : document.fonts;
        if (!fonts?.ready) return;

        fonts.ready.then(
            () => cache.clear(),
            () => {
                // Ignore — stale widths beat crashing.
            },
        );
    }

    return (text: string, font: string): number => {
        hookFontsReadyOnce();

        // Length-prefix so newlines (or anything else) in font/text can't collide two different pairs.
        const cacheKey = `${font.length}:${text.length}:${font}${text}`;
        const cached = cache.get(cacheKey);
        if (cached !== undefined) return cached;

        if (ctx === undefined) {
            ctx = getContext();
        }

        let width: number;
        if (ctx) {
            // We use a single shared canvas. Invalid font strings are ignored, so reset first or we'd measure using the last call's ctx.font
            ctx.font = CANVAS_DEFAULT_FONT;
            ctx.font = font;
            width = ctx.measureText(text).width;
        } else if (whenNoCanvas === "estimate") {
            // No canvas in tests — rough guess from character count and font size.
            width = text.length * parseFontSizePx(font) * 0.6;
        } else {
            width = 0;
        }

        cache.set(cacheKey, width);
        return width;
    };
}

const defaultMeasurer = createTextMeasurer({ whenNoCanvas: "zero" });

/** Cached measurer for truncation. Returns 0 when there is no canvas. */
export function measureTextWidth(text: string, font: string): number {
    return defaultMeasurer(text, font);
}

/** Estimates width in tests when there is no canvas. */
export const layoutTextMeasurer = createTextMeasurer({ whenNoCanvas: "estimate" });

export interface TruncationResult {
    displayText: string;
    wasTruncated: boolean;
}

export function getTruncatedText(stringArray: string[], maxWidth: number, font: string): TruncationResult {
    if (!stringArray.length) {
        return { displayText: "", wasTruncated: false };
    }

    const separator = ", ";
    const ellipsis = ", ...";
    const getMoreText = (count: number) => ` +${count} more`;

    const separatorWidth = measureTextWidth(separator, font);
    const ellipsisWidth = measureTextWidth(ellipsis, font);
    const getMoreTextWidth = (count: number) => measureTextWidth(getMoreText(count), font);

    const visibleZones: string[] = [];
    let currentWidth = 0;

    for (let i = 0; i < stringArray.length; i++) {
        const zoneName = stringArray[i];
        const zoneWidth = measureTextWidth(zoneName, font);
        const isLastZone = i === stringArray.length - 1;

        const newWidth = currentWidth + (visibleZones.length > 0 ? separatorWidth : 0) + zoneWidth;

        const remainingZones = stringArray.length - i - 1;
        const moreTextWidth = remainingZones > 0 ? getMoreTextWidth(remainingZones) : 0;
        const wouldOverflow = newWidth + ellipsisWidth + moreTextWidth > maxWidth;

        if (wouldOverflow && !isLastZone) {
            break;
        }

        visibleZones.push(zoneName);
        currentWidth = newWidth;
    }

    if (visibleZones.length === stringArray.length) {
        return {
            displayText: visibleZones.join(separator),
            wasTruncated: false,
        };
    }

    while (visibleZones.length > 0) {
        const remainingCount = stringArray.length - visibleZones.length;
        const text = visibleZones.join(separator);
        const moreText = getMoreText(remainingCount);

        if (measureTextWidth(text + ellipsis + moreText, font) <= maxWidth) {
            return {
                displayText: text + ellipsis + moreText,
                wasTruncated: true,
            };
        }

        visibleZones.pop();
    }

    return {
        displayText: ellipsis + getMoreText(stringArray.length),
        wasTruncated: true,
    };
}
